import React, { useState, useEffect } from 'react';
import { MailIcon, PhoneIcon, MapPinIcon, SendIcon } from 'lucide-react';
import { sendContactEmail } from '../services/api';
import { ContactForm } from '../types';
import { getContent } from '../config/content';
import { theme } from '../config/theme';
import bouncedadsLogo from '../images/bouncedads1.png';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<ContactForm>({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Handle smooth scroll to FAQ section when hash is present
  useEffect(() => {
    if (window.location.hash === '#faq') {
      const faqSection = document.getElementById('faq');
      if (faqSection) {
        setTimeout(() => {
          faqSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, []);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');
    
    // Format as XXX-XXX-XXXX
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Format phone number with dashes
    if (name === 'phone') {
      processedValue = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await sendContactEmail(formData);
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err: any) {
      // Handle specific error messages from backend
      if (err.response?.detail?.message) {
        setError(err.response.detail.message);
      } else if (err.response?.status === 429) {
        setError('Too many contact form submissions. Please try again later.');
      } else {
        setError('Failed to send message. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${theme.colors.background.secondary} py-8`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="mb-6">
            <img 
              src={bouncedadsLogo} 
              alt={getContent('navigation.logo.alt')} 
              className="h-16 w-16 md:h-20 md:w-20 object-contain mx-auto mb-4"
            />
          </div>
          <h1 className={`text-4xl font-bold ${theme.colors.text.primary} mb-4`}>{getContent('contact.title')}</h1>
          <p className="text-lg text-gray-600">
            {getContent('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">{getContent('contact.getInTouch.title')}</h2>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MailIcon className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: theme.colors.primary[600] }} />
                <div className="min-w-0">
                  <h3 className="font-medium text-gray-900">{getContent('contact.getInTouch.email')}</h3>
                  <p className="text-gray-600">{getContent('company.contact.email')}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <PhoneIcon className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: theme.colors.primary[600] }} />
                <div className="min-w-0">
                  <h3 className="font-medium text-gray-900">{getContent('contact.getInTouch.phone')}</h3>
                  <p className="text-gray-600">{getContent('company.contact.phone')}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <MapPinIcon className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: theme.colors.primary[600] }} />
                <div className="min-w-0">
                  <h3 className="font-medium text-gray-900">{getContent('contact.getInTouch.serviceArea')}</h3>
                  <p className="text-gray-600">
                    {getContent('company.contact.serviceArea')}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 rounded-lg" style={{ backgroundColor: theme.colors.primary[50] }}>
              <h3 className="font-medium mb-2" style={{ color: theme.colors.primary[900] }}>Business Hours</h3>
              <div className="space-y-1" style={{ color: theme.colors.primary[800] }}>
                <p>{getContent('company.businessHours.weekdays')}</p>
                <p>{getContent('company.businessHours.saturday')}</p>
                <p>{getContent('company.businessHours.sunday')}</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">{getContent('contact.form.title')}</h2>
            
            {success && (
              <div className="mb-4 p-4 rounded-md bg-green-50 border border-green-200">
                <p className="text-green-800">{getContent('contact.form.successMessage')}</p>
              </div>
            )}
            
            {error && (
              <div className="mb-4 p-4 rounded-md bg-red-50 border border-red-200">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  {getContent('contact.form.name')} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={getContent('contact.form.namePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  {getContent('contact.form.email')} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={getContent('contact.form.emailPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  {getContent('contact.form.phone')}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={getContent('contact.form.phonePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  {getContent('contact.form.message')} *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={getContent('contact.form.messagePlaceholder')}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: theme.colors.primary[600] }}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {getContent('contact.form.sending')}
                  </>
                ) : (
                  <>
                    <SendIcon className="h-4 w-4 mr-2" />
                    {getContent('contact.form.submit')}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* FAQ Section */}
        <div id="faq" className="mt-12 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">{getContent('contact.faq.title')}</h2>
          <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: theme.colors.primary[50], borderColor: theme.colors.primary[200], borderWidth: '1px' }}>
            <p className="text-sm" style={{ color: theme.colors.primary[800] }}>
              Find answers to common questions about our bounce house rentals and services.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {getContent('contact.faq.questions').map((faq: any, index: number) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                  <span className="text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2" style={{ backgroundColor: theme.colors.primary[600] }}>{index + 1}</span>
                  {faq.question}
                </h3>
                <div className="text-gray-600 whitespace-pre-line">
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
